import { useEffect, useMemo } from 'react';
import { Col, notification, Row, Badge } from 'antd';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks/useApi';
import FinanceInvoices from 'libs/api/finance-invoices';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import { getAllCompanies, getInactiveShops } from 'hooks/useShops';
import { financeInvoicesAdapter } from './invoices.adapter';
import UIHelper from '../../../../helpers/UIHelper';
import moment from 'moment';

const financeInvoicesApi = new FinanceInvoices();
const blankApi = new FinanceInvoices('finance/invoices/blank_invoices');
export const statusColors = {
  created: 'yellow',
  confirm: 'green',
  reject: 'red',
};

// all_companies narrowed to the selected village, then split into active and
// inactive (by inactive_companies ids) for the Insegna filter groups
export const splitShopsForInsegnaFilter = ({
  shops,
  inactiveShops,
  shoppingCenters,
  shoppingCenterId,
}) => {
  const currentShoppingCenterId = parseInt(shoppingCenterId, 10);
  const foundShoppingCenter = (shoppingCenters || []).find(
    (center) => center.id === currentShoppingCenterId,
  );
  const villageShops = foundShoppingCenter
    ? (shops || []).filter((shop) =>
        shop.title.includes(foundShoppingCenter.title),
      )
    : shops || [];
  const inactiveIds = new Set((inactiveShops || []).map((i) => i.id));

  return {
    filteredShops: villageShops.filter((shop) => !inactiveIds.has(shop.id)),
    filteredInactiveShops: villageShops.filter((shop) =>
      inactiveIds.has(shop.id),
    ),
  };
};

const useInvoices = (isMainPage, isBlank = false) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [sp, setSearchParams] = useSearchParams();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const shops = useRecoilValue(getAllCompanies);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const groupBy = sp.get('group_by');
  const includeCompany = sp.get('include_company');
  const api = isBlank ? blankApi : financeInvoicesApi;

  const blankFilters = useMemo(() => ({ blank_invoices: 'on' }), []);

  const {
    data,
    updateInProcess,
    loading,
    onChangeTableFilter,
    handleDownloadExcel,
    onUpdate,
    singleItem,
    setLoading,
    searchParams,
  } = useApi({
    resourceKey: 'invoices',
    singleResourceKey: 'invoices',
    api,
    isMainPage,
    adapter: financeInvoicesAdapter,
    aditionalFilters: undefined,
  });

  const statusTexts = useMemo(
    () => ({
      confirm: t('Confermato'),
      created: t('Nuovo'),
      reject: t('Rifiutato'),
    }),
    [t],
  );

  const { filteredShops, filteredInactiveShops } = useMemo(
    () =>
      splitShopsForInsegnaFilter({
        shops,
        inactiveShops,
        shoppingCenters,
        shoppingCenterId: searchParams.get('shopping_center_id'),
      }),
    [searchParams, shoppingCenters, shops, inactiveShops],
  );
  console.log('filteredShops=', filteredShops.length);

  const columns = useMemo(() => {
    return [
      {
        title: t('Date'),
        dataIndex: 'invoice_date',
        fixed: 'left',
        width: 180,
        sorter: () => {},
        render: (row) => {
          switch (groupBy) {
            case 'day':
              return UIHelper.formatedDate(row, 'DD MMMM YYYY');
            case 'week':
              return `${t('Week')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
            case 'month':
              return UIHelper.formatedDate(row, 'MMMM YYYY');
            case 'year':
              return UIHelper.formatedDate(row, 'YYYY');
            default:
              return `${t('Week')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
          }
        },
      },
      {
        title: t('Insegna'),
        dataIndex: 'company_name',
        sorter: () => {},
      },
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_center_title',
        sorter: () => {},
      },
      {
        title: t('Stato'),
        dataIndex: 'status',
        sorter: () => {},
        render: (data) => (
          <Badge color={statusColors[data]} text={statusTexts[data]} />
        ),
      },
      {
        title: `${t('NETTO')} (€)`,
        dataIndex: 'taxable_sum',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: `${t('LORDO')} (€)`,
        dataIndex: 'taxable_vat_sum',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: t('SCONTRINI'),
        dataIndex: 'receipts_sum',
        sorter: () => {},
      },
      {
        title: t('PEZZI'),
        dataIndex: 'pieces_sum',
        sorter: () => {},
      },
      {
        title: t('Ingressi PdV'),
        dataIndex: 'visitors_sum',
        sorter: () => {},
      },
      //https://kuzevanoff.atlassian.net/jira/software/projects/AR/boards/13?assignee=5aa18678032612267be00884&selectedIssue=AR-645
      // {
      //   title: t('Footfall village'),
      //   dataIndex: 'footfall_village',
      //   // TODO when will be added sorting, uncomment this line
      //   // sorter: () => {},
      // },
      {
        title: `${t('ATV')} €`,
        dataIndex: 'atv_sum',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: t('UPT'),
        // dataIndex: 'upt_sum',
        dataIndex: 'upt_sum',
        sorter: () => {},
      },
      {
        title: t('CR PDV'),
        dataIndex: 'conversion_rate_sum',
        sorter: () => {},
        render: (r) => `${r || 0} %`,
      },
      // https://kuzevanoff.atlassian.net/jira/software/projects/AR/boards/13?assignee=5aa18678032612267be00884&selectedIssue=AR-645
      // {
      //   title: t('CR Village'),
      //   dataIndex: 'cr_village',
      //   render: (r) => `${r || 0} %`,
      // },
      {
        title: t('Chiusura Sett.'),
        dataIndex: 'week_closed',
        sorter: () => {},
        render: (value) => {
          return value ? t('Yes') : t('No');
        },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            (row.can_update !== false && groupBy === 'week')) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && groupBy === 'week' && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ].filter((i) => {
      if (i.dataIndex === 'company_name') {
        return includeCompany === '1';
      }

      return true;
    });
  }, [pathname, t, groupBy, includeCompany, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'shopping_center_id',
        values: shoppingCenters.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'company',
        values: shops?.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'date_from',
        keys: ['date_from', 'date_to'],
      },
      {
        title: t('Periodo'),
        type: 'select',
        name: 'group_by',
        values: [
          { value: '', title: 'Raggruppamento per' },
          { value: 'day', title: t('Giorno') },
          { value: 'week', title: t('Week') },
          { value: 'month', title: t('Mese') },
          { value: 'year', title: t('Anno') },
        ],
      },
      {
        title: t('INCLUDI INSEGNA'),
        type: 'checkbox',
        name: 'include_company',
        values: [{ value: 'on', title: 'test1' }],
      },
    ],
    [t, shoppingCenters, shops],
  );

  const onExportTo = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
        export_to_csv: true,
      };
      await api.onExportToExcel(params);
    } catch (e) {
      console.log('e=', e?.response);
      notification.error({
        message: e?.response?.data?.base?.join(',') || 'Errore',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sp.get('group_by')) {
      setSearchParams({
        group_by: 'week',
        shopping_center_id: '',
      });
    }
  }, []);
  return {
    columns,
    data,
    filters,
    updateInProcess,
    loading,
    onChangeTableFilter,
    onUpdate,
    singleItem,
    onExportTo,
    handleDownloadExcel,
    filteredShops,
    filteredInactiveShops,
    statusTexts,
  };
};

export default useInvoices;
